// react-redux imports
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { useBottomScrollListener } from "react-bottom-scroll-listener";
import { CSSTransition } from "react-transition-group";
import { RootState } from "app/store";
import { setBackgroundColor } from "features/background/backgroundSlice";
import {
  setHeaderAction,
  setHeaderMenuColor,
  setHeaderWidthMode,
} from "features/header/headerSlice";
import { setIsAuthDialogOpen } from "features/authDialog/authDialogSlice";
import AuthDialog from "features/authDialog/AuthDialog";

// common imports
import FlexColumn from "common/components/styled-components/FlexColumn";
import ScrollIndicator from "common/components/ScrollIndicator";
import Stepper, { StepperProps } from "common/components/Stepper";
import StepperContainer from "common/components/styled-components/StepperContainer";
import { getTouchDirection, getWheelDirection } from "common/utils/scroll";
import {
  getInitialSteps,
  getProgressStep,
  getProgressValue,
} from "common/utils/stepper";

import { SECTIONS, Sections } from "features/home/constants/sections";

// styled-component-imports
import Animated from "./styled-components/Animated";
import Container from "./styled-components/Container";
import Content from "./styled-components/Content";
import FactorDescription from "./styled-components/FactorDescription";
import FactorImage from "./styled-components/FactorImage";
import FactorTitle from "./styled-components/FactorTitle";
import PageIndicator from "./styled-components/PageIndicator";
import PageIndicatorGroup from "./styled-components/PageIndicatorGroup";
import Title from "./styled-components/Title";

import { Factor, FACTORS } from "./constants/factors";
import Pagination from "./Pagination";
import { trackCalculationBegins } from "features/cookie-banner/gtm-functions";

function Factors() {
  const dispatch = useDispatch();
  const history = useHistory();
  const [animate, setAnimate] = useState<boolean>(true);
  const [hitTheBottom, setHitTheBottom] = useState<boolean>(false);
  const [factor, setFactor] = useState<Factor>(FACTORS[0]);
  const [touchEvents, setTouchEvents] = useState<React.TouchEvent[]>([]);
  const [wheelEvents, setWheelEvents] = useState<React.WheelEvent[]>([]);
  const [timeoutID, setTimeoutID] = useState<number>();
  const [step, setStep] = useState<number>(1);
  const initialSteps = getInitialSteps<Sections>(SECTIONS).map(
    (sectionStep, index) => ({
      ...sectionStep,
      progress:
        index === SECTIONS.intro.order
          ? 100
          : index === SECTIONS.factors.order
          ? getProgressValue(step, SECTIONS.factors.steps)
          : sectionStep.progress,
    })
  );

  const [stepperData, setStepperData] = useState<StepperProps>({
    steps: initialSteps,
    currentStep: 1,
  });

  const upDesktopScreen = useSelector(
    (state: RootState) => state.app.upDesktopScreen
  );

  const isAuthDialogOpen = useSelector(
    (state: RootState) => state.authDialog.isAuthDialogOpen
  );

  useBottomScrollListener<HTMLDivElement>(
    () => {
      setHitTheBottom(true);
    },
    {
      offset: upDesktopScreen ? 166 : 252,
      debounce: 80,
    }
  );

  function handleAuthClose() {
    dispatch(setIsAuthDialogOpen(false));
  }

  function handleAuthSuccess() {
    history.push("calculator/result", { doNotUpdate: true });
    dispatch(setIsAuthDialogOpen(false));
  }

  function handlePaginationNext() {
    setAnimate(true);
    const { factors: factorsSection } = SECTIONS;
    const nextIndex = FACTORS.indexOf(factor) + 1;
    if (nextIndex < FACTORS.length) {
      setFactor(FACTORS[nextIndex]);
      setStepperData(getProgressStep(stepperData, factorsSection, step + 1));
      setStep(step + 1);
    }
  }

  function handlePaginationBack() {
    setAnimate(true);
    const { factors: factorsSection } = SECTIONS;
    const previousIndex = FACTORS.indexOf(factor) - 1;
    if (previousIndex >= 0) {
      setFactor(FACTORS[previousIndex]);
      setStepperData(getProgressStep(stepperData, factorsSection, step - 1));
      setStep(step - 1);
    }
  }

  function handleScroll(e: React.WheelEvent | React.TouchEvent) {
    window.clearTimeout(timeoutID);
    const { type } = e;
    if (type === "wheel") {
      const events = Array.from(wheelEvents);
      events.push(e as React.WheelEvent);
      setWheelEvents(events);

      const timeout = window.setTimeout(() => {
        if (hitTheBottom) {
          if (getWheelDirection(events) > 0) {
            trackCalculationBegins();
            history.push("/calculator");
          }
        }
        setWheelEvents([]);
      }, 150);
      setTimeoutID(timeout);
    } else if (type === "touchmove") {
      const events = Array.from(touchEvents);
      events.push(e as React.TouchEvent);
      setTouchEvents(events);

      const timeout = window.setTimeout(() => {
        if (hitTheBottom) {
          if (getTouchDirection(events) > 0) {
            trackCalculationBegins();
            history.push("/calculator");
          }
        }
        setTouchEvents([]);
      }, 150);
      setTimeoutID(timeout);
    }
  }

  useEffect(() => {
    dispatch(setBackgroundColor(factor.color));
  }, [factor]);

  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(setHeaderAction("menu"));
    dispatch(setHeaderWidthMode("half"));
    dispatch(setHeaderMenuColor("#ffffff"));

    return () => {
      dispatch(setHeaderWidthMode("full"));
      window.clearTimeout(timeoutID);
    };
  }, []);
  return (
    <CSSTransition
      in={animate}
      classNames="factors"
      timeout={500}
      onEntered={() => setAnimate(false)}
      appear
    >
      <Animated>
        {upDesktopScreen && (
          <StepperContainer left={20}>
            <Stepper {...stepperData} />
          </StepperContainer>
        )}
        {isAuthDialogOpen && (
          <AuthDialog
            handleClose={handleAuthClose}
            successCallback={handleAuthSuccess}
            sendCalculatorData={false}
            isSignInOnly
          />
        )}
        <Container>
          <CSSTransition in={animate} classNames="image" timeout={200} appear>
            <FactorImage src={factor.image} alt={factor.title} />
          </CSSTransition>
          <PageIndicatorGroup>
            {FACTORS.map((_, index) => (
              <PageIndicator
                key={index}
                active={index === FACTORS.indexOf(factor)}
              />
            ))}
          </PageIndicatorGroup>
          <Content onWheel={handleScroll} onTouchMove={handleScroll}>
            <Title>Factores</Title>
            <Pagination
              page={FACTORS.indexOf(factor) + 1}
              total={FACTORS.length}
              handleNext={handlePaginationNext}
              handleBack={handlePaginationBack}
            >
              <FlexColumn gap={8}>
                <FactorTitle>{factor.title}</FactorTitle>
                <FactorDescription>{factor.description}</FactorDescription>
              </FlexColumn>
            </Pagination>
          </Content>
          <ScrollIndicator description="Desliza hacia abajo para comenzar el cálculo de la calculadora" />
        </Container>
      </Animated>
    </CSSTransition>
  );
}

export default Factors;
